/**
 * Cookie Consent Manager v3
 * Google Consent Mode v2 ve Meta Pixel / CAPI uyumlu
 * Kategori bazlı çerez onay sistemi: Zorunlu, Analiz, Pazarlama
 * 
 * Depolama Stratejisi:
 *  - Menta_CookiePreferences (localStorage) → JSON: { necessary, analytics, marketing }
 *  - Menta_CookieConsent (cookie + localStorage) → "true"/"false" (marketing izni, backend uyumluluğu)
 *
 * v3 Değişiklikler:
 *  - Race condition koruması (setTimeout ID takibi + clearTimeout)
 *  - Modal animasyon sınıfları her açılışta sıfırlanır
 *  - ESC tuşuyla modal kapatma desteği
 *  - Granüler API gönderimi (analytics + marketing)
 *  - Tutarlı banner/modal kapatma mantığı
 */

const CookieConsent = (() => {
    // ─── Sabitler ────────────────────────────────────
    const LEGACY_KEY = 'Menta_CookieConsent';
    const PREFS_KEY = 'Menta_CookiePreferences';
    const CONSENT_TYPE = 5; // ConsentType.CookiePolicy
    const BANNER_ID = 'cookie-consent-banner';
    const MODAL_ID = 'cookie-preferences-modal';
    const API_ENDPOINT = '/api/Public/Consent/Submit';

    // Varsayılan tercihler (hiçbir izin verilmemiş)
    const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

    // ─── Race Condition Koruması ─────────────────────
    // Aktif setTimeout ID'leri — yeni işlem başlamadan önce eskileri iptal etmek için
    var _bannerTimerId = null;
    var _modalTimerId = null;

    function _clearBannerTimer() {
        if (_bannerTimerId !== null) {
            clearTimeout(_bannerTimerId);
            _bannerTimerId = null;
        }
    }

    function _clearModalTimer() {
        if (_modalTimerId !== null) {
            clearTimeout(_modalTimerId);
            _modalTimerId = null;
        }
    }

    // ─── Tercih Okuma / Yazma ────────────────────────

    /**
     * Kaydedilmiş çerez tercihlerini oku.
     * Önce yeni granüler format (Menta_CookiePreferences), sonra eski format (Menta_CookieConsent).
     * @returns {{ necessary: boolean, analytics: boolean, marketing: boolean } | null}
     */
    function getPreferences() {
        try {
            // 1. Yeni granüler format
            var prefsStr = localStorage.getItem(PREFS_KEY);
            if (prefsStr) {
                var prefs = JSON.parse(prefsStr);
                if (typeof prefs === 'object' && prefs !== null) {
                    return {
                        necessary: true,
                        analytics: prefs.analytics === true,
                        marketing: prefs.marketing === true
                    };
                }
            }

            // 2. Eski format (boolean) — geriye dönük uyumluluk
            var cookieMatch = document.cookie.match(new RegExp('(^|;\\s*)' + LEGACY_KEY + '=([^;]+)'));
            if (cookieMatch) {
                var val = cookieMatch[2];
                if (val === 'true') return { necessary: true, analytics: true, marketing: true };
                if (val === 'false') return { necessary: true, analytics: false, marketing: false };
            }

            var stored = localStorage.getItem(LEGACY_KEY);
            if (stored !== null) {
                var parsed = JSON.parse(stored);
                if (parsed === true) return { necessary: true, analytics: true, marketing: true };
                if (parsed === false) return { necessary: true, analytics: false, marketing: false };
            }

            return null; // Henüz karar verilmemiş
        } catch (e) {
            return null;
        }
    }

    /**
     * Eski API uyumluluğu: true/false/null döner (marketing izni bazlı)
     */
    function getConsentStatus() {
        var prefs = getPreferences();
        if (prefs === null) return null;
        return prefs.marketing;
    }

    /**
     * Pazarlama çerezleri izni var mı?
     */
    function hasMarketingConsent() {
        var prefs = getPreferences();
        return prefs !== null && prefs.marketing === true;
    }

    /**
     * Analiz çerezleri izni var mı?
     */
    function hasAnalyticsConsent() {
        var prefs = getPreferences();
        return prefs !== null && prefs.analytics === true;
    }

    /**
     * Herhangi bir çerez kararı verilmiş mi?
     */
    function hasConsentDecision() {
        return getPreferences() !== null;
    }

    /**
     * Tercihleri kaydet: hem yeni granüler hem eski boolean format.
     * Önce eski formatları temizle, sonra yeni formatı yaz — tutarsızlık önlenir.
     */
    function savePreferences(prefs) {
        // 1. Önce eski formatları temizle (stale data'yı önle)
        try {
            localStorage.removeItem(LEGACY_KEY);
        } catch (e) { }

        // 2. Granüler format (localStorage) — birincil kaynak
        try {
            localStorage.setItem(PREFS_KEY, JSON.stringify({
                necessary: true,
                analytics: prefs.analytics === true,
                marketing: prefs.marketing === true
            }));
        } catch (e) { }

        // 3. Eski boolean format (cookie) — backend MetaCapiService uyumluluğu
        try {
            var maxAge = 365 * 24 * 60 * 60; // 1 yıl
            document.cookie = LEGACY_KEY + '=' + (prefs.marketing ? 'true' : 'false') + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
        } catch (e) { }
    }

    // ─── Consent Uygulama ────────────────────────────

    /**
     * Google Consent Mode v2 güncelle
     */
    function applyGoogleConsent(prefs) {
        window.dataLayer = window.dataLayer || [];
        if (typeof gtag === 'function') {
            gtag('consent', 'update', {
                'analytics_storage': prefs.analytics ? 'granted' : 'denied',
                'ad_storage': prefs.marketing ? 'granted' : 'denied',
                'ad_user_data': prefs.marketing ? 'granted' : 'denied',
                'ad_personalization': prefs.marketing ? 'granted' : 'denied'
            });
        }
    }

    /**
     * Meta Pixel izin durumunu güncelle
     */
    function applyMetaConsent(prefs) {
        if (typeof fbq === 'function') {
            if (prefs.marketing) {
                fbq('consent', 'grant');
            } else {
                fbq('consent', 'revoke');
            }
        }
    }

    /**
     * dataLayer'a consent olayı gönder
     */
    function pushConsentEvent(prefs) {
        window.dataLayer = window.dataLayer || [];
        var testCode = (typeof window.getMetaTestEventCode === 'function' ? window.getMetaTestEventCode() : null);
        var currentUrl = window.location.href;

        var eventName = 'cookie_consent_denied';
        if (prefs.marketing && prefs.analytics) {
            eventName = 'cookie_consent_granted';
        } else if (prefs.marketing || prefs.analytics) {
            eventName = 'cookie_consent_updated';
        }

        var payload = {
            'event': eventName,
            'consent_analytics_storage': prefs.analytics ? 'granted' : 'denied',
            'consent_ad_storage': prefs.marketing ? 'granted' : 'denied',
            'consent_ad_user_data': prefs.marketing ? 'granted' : 'denied',
            'consent_ad_personalization': prefs.marketing ? 'granted' : 'denied',
            'event_source_url': currentUrl,
            'page_location': currentUrl,
            'page_path': window.location.pathname,
            'page_title': document.title,
            'meta_event_source_url': currentUrl,
            'action_source': 'website',
            'event_time': Math.floor(Date.now() / 1000)
        };
        if (testCode) {
            payload.test_event_code = testCode;
            payload.meta_test_event_code = testCode;
        }
        window.dataLayer.push(payload);
    }

    /**
     * Tüm consent ayarlarını uygula (Google + Meta + dataLayer)
     */
    function applyConsent(prefs) {
        applyGoogleConsent(prefs);
        applyMetaConsent(prefs);
        pushConsentEvent(prefs);

        // Pazarlama izni verildiyse bekleyen olayları tetikle
        if (prefs.marketing) {
            try {
                window.dispatchEvent(new CustomEvent('menta:consentGranted'));
            } catch (e) { }
        }
    }

    /**
     * Eski API uyumluluğu: grantConsent (tüm izinleri ver)
     */
    function grantConsent() {
        var prefs = { necessary: true, analytics: true, marketing: true };
        applyConsent(prefs);
    }

    /**
     * Eski API uyumluluğu: denyConsent (tüm izinleri reddet)
     */
    function denyConsent() {
        var prefs = { necessary: true, analytics: false, marketing: false };
        applyGoogleConsent(prefs);
        applyMetaConsent(prefs);
    }

    /**
     * API'ye onay durumunu bildir (granüler tercihlerle)
     */
    async function submitToApi(prefs) {
        try {
            await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Type: CONSENT_TYPE,
                    Accepted: prefs.marketing === true,
                    Analytics: prefs.analytics === true
                })
            });
        } catch (e) { }
    }

    // ─── Banner UI ───────────────────────────────────

    function closeBanner() {
        _clearBannerTimer();
        var banner = document.getElementById(BANNER_ID);
        if (!banner) return;

        banner.classList.add('opacity-0', 'translate-y-10', 'pointer-events-none');
        _bannerTimerId = setTimeout(function () {
            _bannerTimerId = null;
            banner.classList.add('hidden');
            banner.style.display = 'none';
        }, 300);
    }

    function showBanner() {
        _clearBannerTimer();
        var banner = document.getElementById(BANNER_ID);
        if (!banner) return;

        banner.style.display = '';
        banner.classList.remove('hidden');
        _bannerTimerId = setTimeout(function () {
            _bannerTimerId = null;
            banner.classList.remove('opacity-0', 'translate-y-10', 'pointer-events-none');
        }, 50);
    }

    // ─── Preferences Modal UI ────────────────────────

    function showPreferencesModal() {
        closeBanner();

        // Race condition koruması: eski kapama timer'ı varsa iptal et
        _clearModalTimer();

        var modal = document.getElementById(MODAL_ID);
        if (!modal) return;

        var overlay = modal.querySelector('.cookie-modal-overlay');
        var panel = modal.querySelector('.cookie-modal-panel');

        // Mevcut tercihleri toggle'lara yükle — HER AÇILIŞTA localStorage'dan güncel oku
        var prefs = getPreferences() || DEFAULT_PREFS;
        var analyticsToggle = document.getElementById('cookie-pref-analytics');
        var marketingToggle = document.getElementById('cookie-pref-marketing');

        if (analyticsToggle) {
            analyticsToggle.checked = prefs.analytics;
        }
        if (marketingToggle) {
            marketingToggle.checked = prefs.marketing;
        }

        // Animasyon sınıflarını sıfırla — önce temizle, sonra başlangıç konumuna getir
        if (overlay) {
            overlay.classList.remove('opacity-0', 'opacity-100');
            overlay.classList.add('opacity-0');
        }
        if (panel) {
            panel.classList.remove('opacity-0', 'opacity-100', 'scale-95', 'scale-100', 'translate-y-4', 'translate-y-0');
            panel.classList.add('opacity-0', 'scale-95', 'translate-y-4');
        }

        // Modal'ı göster
        modal.classList.remove('hidden');
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // Bir sonraki frame'de animasyonu başlat
        _modalTimerId = setTimeout(function () {
            _modalTimerId = null;
            if (overlay) {
                overlay.classList.remove('opacity-0');
                overlay.classList.add('opacity-100');
            }
            if (panel) {
                panel.classList.remove('opacity-0', 'scale-95', 'translate-y-4');
                panel.classList.add('opacity-100', 'scale-100', 'translate-y-0');
            }
        }, 20);
    }

    function closePreferencesModal() {
        // Race condition koruması: eski açma timer'ı varsa iptal et
        _clearModalTimer();

        var modal = document.getElementById(MODAL_ID);
        if (!modal) return;

        var overlay = modal.querySelector('.cookie-modal-overlay');
        var panel = modal.querySelector('.cookie-modal-panel');

        if (overlay) {
            overlay.classList.remove('opacity-100');
            overlay.classList.add('opacity-0');
        }
        if (panel) {
            panel.classList.remove('opacity-100', 'scale-100', 'translate-y-0');
            panel.classList.add('opacity-0', 'scale-95', 'translate-y-4');
        }

        document.body.style.overflow = '';

        _modalTimerId = setTimeout(function () {
            _modalTimerId = null;
            modal.classList.add('hidden');
            modal.style.display = 'none';
        }, 300);
    }

    // ─── Aksiyon Yöneticileri ────────────────────────

    function handleAcceptAll() {
        var prefs = { necessary: true, analytics: true, marketing: true };
        savePreferences(prefs);
        applyConsent(prefs);
        closeBanner();
        closePreferencesModal();
        submitToApi(prefs);
    }

    function handleRejectAll() {
        var prefs = { necessary: true, analytics: false, marketing: false };
        savePreferences(prefs);
        applyConsent(prefs);
        closeBanner();
        closePreferencesModal();
        submitToApi(prefs);
    }

    function handleSavePreferences() {
        var analyticsToggle = document.getElementById('cookie-pref-analytics');
        var marketingToggle = document.getElementById('cookie-pref-marketing');

        var prefs = {
            necessary: true,
            analytics: analyticsToggle ? analyticsToggle.checked : false,
            marketing: marketingToggle ? marketingToggle.checked : false
        };

        savePreferences(prefs);
        applyConsent(prefs);
        closeBanner();
        closePreferencesModal();
        submitToApi(prefs);
    }

    /**
     * Dışarıdan çağrılabilir: Tercih panelini aç (footer bağlantısı vb.)
     */
    function resetConsent() {
        showPreferencesModal();
    }

    // ─── Yardımcılar ─────────────────────────────────

    function bindButton(id, handler) {
        var btn = document.getElementById(id);
        if (btn) {
            btn.removeEventListener('click', handler);
            btn.addEventListener('click', handler);
        }
    }

    // ─── Başlatma ────────────────────────────────────

    function init() {
        var prefs = getPreferences();

        if (prefs === null) {
            // Henüz karar verilmemiş: varsayılan tümü reddedilmiş, banner göster
            applyGoogleConsent(DEFAULT_PREFS);
            applyMetaConsent(DEFAULT_PREFS);
            showBanner();
        } else {
            // Karar mevcut: sessizce uygula
            applyGoogleConsent(prefs);
            applyMetaConsent(prefs);
            closeBanner();
        }

        // Banner butonlarını bağla
        bindButton('cookie-consent-accept', handleAcceptAll);
        bindButton('cookie-consent-reject', handleRejectAll);
        bindButton('cookie-consent-customize', showPreferencesModal);

        // Modal butonlarını bağla
        bindButton('cookie-pref-save', handleSavePreferences);
        bindButton('cookie-pref-accept-all', handleAcceptAll);
        bindButton('cookie-pref-reject-all', handleRejectAll);
        bindButton('cookie-pref-close', closePreferencesModal);

        // Modal arka planına tıklayınca kapat
        var modal = document.getElementById(MODAL_ID);
        if (modal) {
            modal.addEventListener('click', function (e) {
                if (e.target === modal || e.target.classList.contains('cookie-modal-overlay')) {
                    closePreferencesModal();
                }
            });
        }

        // ESC tuşuyla modal kapatma
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' || e.keyCode === 27) {
                var activeModal = document.getElementById(MODAL_ID);
                if (activeModal && activeModal.style.display === 'flex') {
                    closePreferencesModal();
                }
            }
        });

        // Doğrudan footer butonunu da bağla
        bindButton('btn-manage-cookies', function (e) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            showPreferencesModal();
        });
    }

    // Global document event delegation for opening cookie preferences (hem capture hem bubble aşamasında dinle)
    function onCookiePrefTriggerClick(e) {
        var target = e.target && e.target.closest ? e.target.closest('#btn-manage-cookies, [data-action="open-cookie-preferences"], .js-open-cookie-preferences') : null;
        if (target) {
            e.preventDefault();
            e.stopPropagation();
            showPreferencesModal();
        }
    }
    document.addEventListener('click', onCookiePrefTriggerClick, true);
    document.addEventListener('click', onCookiePrefTriggerClick, false);

    // ─── Public API ──────────────────────────────────

    return {
        init: init,
        getPreferences: getPreferences,
        getConsentStatus: getConsentStatus,
        hasMarketingConsent: hasMarketingConsent,
        hasAnalyticsConsent: hasAnalyticsConsent,
        hasConsentDecision: hasConsentDecision,
        grantConsent: grantConsent,
        denyConsent: denyConsent,
        closeBanner: closeBanner,
        showBanner: showBanner,
        showPreferencesModal: showPreferencesModal,
        closePreferencesModal: closePreferencesModal,
        resetConsent: resetConsent,
        handleAcceptAll: handleAcceptAll,
        handleRejectAll: handleRejectAll
    };
})();

// Global window erişimi sağla
window.CookieConsent = CookieConsent;
window.openCookiePreferences = function () {
    CookieConsent.showPreferencesModal();
};

// Sayfa yüklendiğinde çalıştır
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', CookieConsent.init);
} else {
    CookieConsent.init();
}
